import { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/project/karuna/components/ui/dialog"; // @ts-ignore - dialog is not a valid module
import { Loader2, Eye } from "lucide-react"; // @ts-ignore - lucide-react is not a valid module
import { getUserInfo, setLocalUserInfo } from "@/lib/storeManager";
import { createForm } from "@formily/core";
import { useQuery } from "@tanstack/react-query";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import {
  SchemaService,
  DynamicModuleService,
  TenantUserService,
} from "@/services/APIs";
import { removeMetaFields } from "@/lib/removeMetaFields";

// Schema names
const SCHEMA_NAME = "School Profile";

export function SchoolProfileForm() {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [hasSignupData, setHasSignupData] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // Create a separate form for view-only mode
  const viewForm = useMemo(() => createForm({}), []);

  // Create form using Formily
  const form = useMemo(() => createForm({}), []);

  // Fetch schema with error handling
  const {
    data: schemaData,
    isLoading: schemaLoading,
    error: schemaError,
    isError: isSchemaError,
  } = useQuery({
    queryKey: ["schema", SCHEMA_NAME],
    queryFn: () => SchemaService.getSchemaByName(SCHEMA_NAME),
    enabled: !!SCHEMA_NAME,
    retry: false, // Don't retry if schema doesn't exist
  });

  // Log schema structure for debugging
  useEffect(() => {
    if (schemaData) {
      const schemaJson = schemaData.json ?? schemaData;
      const schema = (schemaJson as any)?.schema ?? schemaJson;
      if (schema && typeof schema === "object" && schema.properties) {
        const fieldNames = Object.keys(schema.properties);
        console.log("📋 Schema field names:", fieldNames);
      }
    }
  }, [schemaData]);

  // Fetch existing data and populate form
  useEffect(() => {
    fetchExistingData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to normalize mobile number
  const normalizeMobileNumber = (mobileNumber: string): string => {
    return String(mobileNumber || "")
      .replace(/\D/g, "")
      .slice(0, 10);
  };

  const fetchExistingData = async () => {
    try {
      // Get user info from localStorage (from login/signup)
      const userInfo = getUserInfo();
      console.log("🔍 UserInfo from localStorage:", userInfo);

      const signupMobileNumber = userInfo?.phone_number ?? "";
      const loggedInMobileNumber = normalizeMobileNumber(signupMobileNumber);

      if (!loggedInMobileNumber) {
        console.log("⚠️ No mobile number found in user info, showing form");
        setFetchingData(false);
        return;
      }

      console.log("📝 Logged in mobile number:", loggedInMobileNumber);

      // Get school name and contact from userInfo (stored during signup/login)
      // Also try to fetch from /api/users/ if not in localStorage
      let schoolNameFromSignup = userInfo?.signup_school_name ?? "";
      const contactNumberFromSignup = loggedInMobileNumber; // Use from userInfo

      // If school name is missing, try to fetch from /api/users/
      if (!schoolNameFromSignup && loggedInMobileNumber) {
        try {
          console.log(
            "🔍 School name not in localStorage, fetching from /api/users/..."
          );
          const usersResponse = await TenantUserService.listTenantUsers(1, 100);
          const usersList =
            usersResponse?.users ?? usersResponse?.results ?? [];

          if (Array.isArray(usersList)) {
            const matchingUser = usersList.find((user: any) => {
              const userPhone = normalizeMobileNumber(user.phone_number ?? "");
              return userPhone === loggedInMobileNumber && userPhone.length > 0;
            });

            if (matchingUser) {
              // Get school name from first_name (where we store it)
              // Also check school_name field if it exists
              schoolNameFromSignup =
                (matchingUser as any).school_name ??
                matchingUser.first_name ??
                "";
              console.log("✅ Found user in /api/users/:", {
                id: matchingUser.id,
                email: matchingUser.email,
                first_name: matchingUser.first_name,
                school_name: (matchingUser as any).school_name,
                phone_number: matchingUser.phone_number,
              });
              console.log("✅ Extracted school name:", schoolNameFromSignup);

              // Update userInfo in localStorage with school name
              if (schoolNameFromSignup) {
                const updatedUserInfo = {
                  ...userInfo,
                  signup_school_name: schoolNameFromSignup,
                };
                setLocalUserInfo(updatedUserInfo);
                console.log(
                  "💾 Updated userInfo in localStorage with school name"
                );
              }
            }
          }
        } catch (fetchError) {
          console.warn(
            "⚠️ Could not fetch school name from /api/users/:",
            fetchError
          );
        }
      }

      console.log("📝 Using signup data:");
      console.log("   - School Name:", schoolNameFromSignup);
      console.log("   - Contact Number:", contactNumberFromSignup);

      const hasSignup = !!(schoolNameFromSignup ?? contactNumberFromSignup);
      setHasSignupData(hasSignup);

      // Pre-populate signup data (both school name and contact from userInfo)
      if (hasSignup) {
        const initialValues: any = {};
        if (schoolNameFromSignup) {
          initialValues.schoolname = schoolNameFromSignup;
        }
        if (contactNumberFromSignup) {
          initialValues.contact = contactNumberFromSignup;
        }
        form.setValues(initialValues);
        console.log(
          "✅ Pre-populated signup data from userInfo:",
          initialValues
        );
      }

      // CRITICAL: Check if entry exists in "School Profile" schema for this mobile number
      // User can only submit profile once - if entry exists, show table view
      if (!loggedInMobileNumber) {
        console.log("⚠️ No mobile number found in user info, showing form");
        setFetchingData(false);
        return;
      }

      console.log(
        "🔍 Checking for existing profile entry for mobile:",
        loggedInMobileNumber
      );

      // Fetch all records from School Profile schema to check for existing entry
      let allRecords: any[] = [];

      try {
        // Try multiple methods to fetch records (same robust method as login/signup)
        // Method 1: Try getGridColItems (but it might not return all fields)
        try {
          console.log("🔍 Trying getGridColItems (chunked)...");
          allRecords =
            await DynamicModuleService.getGridColItemsAllPages(SCHEMA_NAME);
          console.log(
            `✅ getGridColItemsAllPages: Found ${allRecords.length} records`
          );
          if (allRecords.length > 0) {
            console.log(
              "📋 Fields in getGridColItems record:",
              Object.keys(allRecords[0])
            );
          }
        } catch (gridError) {
          console.warn(
            "⚠️ getGridColItems failed, trying getGridCoByConfig:",
            gridError
          );
        }

        // Note: We don't need getGridCoByConfig anymore since we use getItemById to fetch full records
        // This avoids "Column not found in schema" errors

        console.log(`🔍 Total records fetched: ${allRecords.length}`);

        // Check ALL records for exact mobile number match
        const existingRecord = allRecords.find((record: any) => {
          const recordContact = String(record.contact ?? "").trim();
          const normalizedRecordContact = normalizeMobileNumber(recordContact);
          const matches = normalizedRecordContact === loggedInMobileNumber;

          if (matches) {
            console.log(
              `✅ EXISTING PROFILE FOUND! Record ID: ${record.id}, Contact: "${recordContact}"`
            );
          }

          return matches;
        });

        if (existingRecord) {
          // Entry exists - fetch full data by ID to get all fields (like AdminSchools does)
          const recordId = existingRecord.id ?? existingRecord._id;
          let recordValues = removeMetaFields(existingRecord);

          // Fetch full data by ID to ensure we get all fields including principal
          if (recordId) {
            try {
              console.log("🔍 Fetching full record data by ID:", recordId);
              const fullData = await DynamicModuleService.getItemById(
                SCHEMA_NAME,
                recordId
              );
              const fullRecordData =
                fullData?.data ?? fullData ?? existingRecord;
              recordValues = removeMetaFields(fullRecordData);
              console.log("✅ Fetched full record data:", recordValues);
            } catch (fetchError: any) {
              // Silently handle errors - don't show toast for "Column not found" errors
              const errorMessage =
                fetchError?.response?.data?.message ??
                fetchError?.message ??
                "";
              if (
                errorMessage.includes("Column not found") ||
                errorMessage.includes("column not found")
              ) {
                console.warn(
                  "⚠️ Column not found error (expected if field doesn't exist in DB), using existing record"
                );
              } else {
                console.warn(
                  "⚠️ Could not fetch full data by ID, using existing record:",
                  fetchError
                );
              }
              // Continue with existing record if fetch fails
            }
          }

          console.log("📋 Raw record values before mapping:", recordValues);

          // Map field names to handle different schema field name variations
          // Some schemas use "principalname" instead of "principal"
          if (recordValues.principalname && !recordValues.principal) {
            recordValues.principal = recordValues.principalname;
            console.log(
              "✅ Mapped principalname to principal:",
              recordValues.principalname
            );
          }
          // Also check for other possible variations
          if (recordValues.principal_name && !recordValues.principal) {
            recordValues.principal = recordValues.principal_name;
            console.log(
              "✅ Mapped principal_name to principal:",
              recordValues.principal_name
            );
          }

          // Log principal field specifically
          console.log("📋 Principal field value:", recordValues.principal);
          console.log("📋 All mapped record values:", recordValues);

          setFormValues(recordValues);
          form.setValues(recordValues);
          setIsSubmitted(true);
          console.log("✅ Showing submitted view - profile already exists");
        } else {
          // No entry found - show form (user can submit profile)
          console.log(
            "ℹ️ No existing profile found for this mobile number, showing form"
          );
          setIsSubmitted(false);
        }
      } catch (schemaError: any) {
        console.warn(
          "⚠️ Could not fetch existing records from schema:",
          schemaError
        );
        // If we can't check, show form (allow user to try submitting)
        setIsSubmitted(false);
      }
    } catch (error) {
      console.error("❌ Error fetching data:", error);
      // Ensure signup data is still populated even on error
      const userInfo = getUserInfo();
      const signupSchoolName = userInfo?.signup_school_name ?? "";
      const signupMobileNumber = userInfo?.phone_number ?? "";
      if (signupSchoolName || signupMobileNumber) {
        const initialValues: any = {};
        if (signupSchoolName) {
          initialValues.schoolname = signupSchoolName;
        }
        if (signupMobileNumber) {
          initialValues.contact = normalizeMobileNumber(signupMobileNumber);
        }
        form.setValues(initialValues);
        setHasSignupData(true);
      }
      // On error, show form (allow user to try)
      setIsSubmitted(false);
    } finally {
      setFetchingData(false);
    }
  };

  // Remove "status" field from schema for schools (hide it from the form)
  // This must be called before any conditional returns to follow Rules of Hooks
  const schemaJson = useMemo(() => {
    if (!schemaData) {
      return null;
    }
    const rawSchemaJson = schemaData.json ?? schemaData;

    if (!rawSchemaJson || typeof rawSchemaJson !== "object") {
      return rawSchemaJson;
    }

    // Recursive function to remove status field from nested structures
    const removeStatusField = (obj: any): any => {
      if (!obj || typeof obj !== "object") {
        return obj;
      }

      // If it's an array, process each item
      if (Array.isArray(obj)) {
        return obj.map(item => removeStatusField(item));
      }

      // Create a copy of the object
      const result = { ...obj };

      // If this object has properties, check and remove status
      if (result.properties && typeof result.properties === "object") {
        const newProperties = { ...result.properties };
        // Remove status field from properties
        delete newProperties.status;

        // Recursively process nested properties
        Object.keys(newProperties).forEach(key => {
          newProperties[key] = removeStatusField(newProperties[key]);
        });

        result.properties = newProperties;
      }

      // Also check for nested schema
      if (result.schema) {
        result.schema = removeStatusField(result.schema);
      }

      return result;
    };

    const schema = (rawSchemaJson as any)?.schema ?? rawSchemaJson;
    const modifiedSchema = removeStatusField(schema);

    // Return in the same structure as input
    if ((rawSchemaJson as any)?.schema) {
      return {
        ...(rawSchemaJson as object),
        schema: modifiedSchema,
      };
    }
    return modifiedSchema;
  }, [schemaData]);

  // Hide status field for schools (always hidden, not just when submitted)
  useEffect(() => {
    if (schemaData) {
      // Hide status field using Formily field state
      form.setFieldState("status", state => {
        state.visible = false;
        state.display = "none";
      });
    }
  }, [form, schemaData]);

  // Set field states for disabled fields (signup data)
  // This must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (hasSignupData && schemaData) {
      form.setFieldState("schoolname", state => {
        state.disabled = true;
        const existingStyle = state.componentProps?.style ?? {};
        const existingClassName = state.componentProps?.className ?? "";
        state.componentProps = {
          ...state.componentProps,
          disabled: true,
          style: {
            ...existingStyle,
            color: "#707070",
          },
          className: `${existingClassName} disabled-field`.trim(),
        };
      });
      form.setFieldState("contact", state => {
        state.disabled = true;
        const existingStyle = state.componentProps?.style ?? {};
        const existingClassName = state.componentProps?.className ?? "";
        state.componentProps = {
          ...state.componentProps,
          disabled: true,
          style: {
            ...existingStyle,
            color: "#707070",
          },
          className: `${existingClassName} disabled-field`.trim(),
        };
      });
    }
  }, [hasSignupData, form, schemaData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("🖱️ Submit button clicked");
    setLoading(true);

    try {
      // Validate form
      await form.validate();

      // Get form values
      const values = removeMetaFields(form.values);
      console.log("📋 Current form data:", values);

      // Transform field names if needed
      const transformedValues: Record<string, any> = { ...values };

      // School self-registration must queue for admin review (admin "Add School" sets approved separately).
      transformedValues.status = "pending";

      console.log(
        "💾 Attempting to save to schema:",
        SCHEMA_NAME,
        transformedValues
      );

      // Save to schema
      const schemaResponse = await DynamicModuleService.createItem(
        SCHEMA_NAME,
        transformedValues
      );
      console.log("✅ Saved to School Profile schema:", schemaResponse);

      // Store the response data (which may include the created record)
      const savedRecord = schemaResponse?.data ?? schemaResponse;
      if (savedRecord) {
        const recordValues = removeMetaFields(savedRecord);

        // Map field names to handle different schema field name variations
        if (recordValues.principalname && !recordValues.principal) {
          recordValues.principal = recordValues.principalname;
        }
        if (recordValues.principal_name && !recordValues.principal) {
          recordValues.principal = recordValues.principal_name;
        }

        console.log("📋 Saved record values after mapping:", recordValues);
        setFormValues(recordValues);
        form.setValues(recordValues);
      } else {
        setFormValues(values);
      }

      setIsSubmitted(true);
    } catch (error: any) {
      console.error("❌ Error in handleSubmit:", error);
      console.error("❌ Error details:", JSON.stringify(error, null, 2));
      console.error("❌ Response data:", error?.response?.data);

      // Try to extract detailed error message from backend
      let errorMessage = "Failed to save profile. Please try again.";

      if (error?.response?.data) {
        const responseData = error.response.data;

        // Handle different error response formats
        if (typeof responseData === "string") {
          errorMessage = responseData;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else if (responseData.non_field_errors) {
          errorMessage = Array.isArray(responseData.non_field_errors)
            ? responseData.non_field_errors.join(", ")
            : responseData.non_field_errors;
        } else if (typeof responseData === "object") {
          // Try to extract field-specific errors
          const fieldErrors: string[] = [];
          Object.keys(responseData).forEach(key => {
            if (Array.isArray(responseData[key])) {
              fieldErrors.push(`${key}: ${responseData[key].join(", ")}`);
            } else if (typeof responseData[key] === "string") {
              fieldErrors.push(`${key}: ${responseData[key]}`);
            }
          });
          if (fieldErrors.length > 0) {
            errorMessage = fieldErrors.join("; ");
          }
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      console.error("❌ Final error message:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData || schemaLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  // Handle schema error or missing schema
  if (isSchemaError || (!schemaLoading && !schemaData)) {
    const errorAny = schemaError as any;
    const errorMessage =
      errorAny?.response?.data?.detail ??
      errorAny?.response?.data?.message ??
      errorAny?.message ??
      "Schema not found";

    console.error("❌ Schema Error:", errorMessage);
    console.error("❌ Full error:", schemaError);

    return (
      <Card>
        <CardHeader>
          <CardTitle>School Profile</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center space-y-4 py-12">
          <p className="font-medium text-destructive">
            Schema Error: {errorMessage}
          </p>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            The &quot;School Profile&quot; schema could not be loaded. Please
            ensure the schema exists in the system or contact support.
          </p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>Schema Name: &quot;{SCHEMA_NAME}&quot;</p>
            <p>
              If this is a new installation, please create the schema first.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get schema JSON (schemaData is guaranteed to exist here due to check above)
  if (!schemaData || !schemaJson) {
    return null; // Should not reach here, but TypeScript safety
  }

  // Show table view after submission
  if (isSubmitted) {
    // Get status from formValues
    const profileStatus = formValues.status ?? "pending";

    // Determine message and styling based on status
    let statusMessage = "Your school profile has been submitted for approval";
    let statusClassName = "text-yellow-700 bg-yellow-100 p-4 rounded-md";

    if (profileStatus === "approved") {
      statusMessage = "Your school profile has been approved";
      statusClassName = "text-green-700 bg-green-100 p-4 rounded-md";
    } else if (profileStatus === "rejected") {
      statusMessage = formValues.rejection_reason
        ? `Your school profile has been rejected. Reason: ${formValues.rejection_reason}`
        : "Your school profile has been rejected";
      statusClassName = "text-red-700 bg-red-100 p-4 rounded-md";
    } else {
      // pending or no status
      statusMessage = "Your school profile has been submitted for approval";
      statusClassName = "text-yellow-700 bg-yellow-100 p-4 rounded-md";
    }

    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle>School Profile</CardTitle>
            <CardDescription className={statusClassName}>
              {statusMessage}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] border-collapse border border-border">
                <thead>
                  <tr>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      KC No
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      School Name
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Principal&apos;s Name
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Contact Number
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      School Email
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Nearest Karuna Kendra
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Teacher Name
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Teacher Mobile
                    </th>
                    <th className="whitespace-nowrap border-b border-r border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Teacher Email
                    </th>
                    <th className="whitespace-nowrap border-b border-border bg-muted/50 px-2 py-2 text-xs font-medium text-muted-foreground sm:px-4 sm:py-3 sm:text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.kcno ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.schoolname ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.principal ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.contact ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.schoolemail ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.kendra ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.teachername ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.teachercontact ?? "—"}
                    </td>
                    <td className="border-r border-border px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      {formValues.teacheremail ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-center text-xs sm:px-4 sm:py-3 sm:text-sm">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          console.log(
                            "👁️ View button clicked, formValues:",
                            formValues
                          );
                          console.log("👁️ Schema data:", schemaData);

                          // Set form values for view-only mode (excluding status)
                          const {
                            status: _status,
                            ...formValuesWithoutStatus
                          } = formValues;

                          // Ensure principal field is mapped for view form as well
                          if (
                            formValuesWithoutStatus.principalname &&
                            !formValuesWithoutStatus.principal
                          ) {
                            formValuesWithoutStatus.principal =
                              formValuesWithoutStatus.principalname;
                          }
                          if (
                            formValuesWithoutStatus.principal_name &&
                            !formValuesWithoutStatus.principal
                          ) {
                            formValuesWithoutStatus.principal =
                              formValuesWithoutStatus.principal_name;
                          }

                          console.log(
                            "👁️ Setting view form values:",
                            formValuesWithoutStatus
                          );
                          console.log(
                            "👁️ Principal in view form:",
                            formValuesWithoutStatus.principal
                          );
                          viewForm.setValues(formValuesWithoutStatus);

                          // Explicitly hide the status field if it exists
                          viewForm.setFieldState("status", state => {
                            state.visible = false;
                            state.display = "none";
                          });

                          // Disable all fields - get all field names from schema
                          // Use the modified schema (status field already removed)
                          const schema =
                            (schemaJson as any)?.schema ?? schemaJson;

                          if (
                            schema &&
                            typeof schema === "object" &&
                            schema.properties
                          ) {
                            // Get all field names from schema
                            const getAllFieldNames = (
                              props: any,
                              prefix = ""
                            ): string[] => {
                              const fields: string[] = [];
                              Object.keys(props).forEach(key => {
                                const prop = props[key];
                                if (prop.properties) {
                                  // Nested object, recurse
                                  const nestedFields = getAllFieldNames(
                                    prop.properties,
                                    prefix ? `${prefix}.${key}` : key
                                  );
                                  fields.push(...nestedFields);
                                } else if (prop.name || key) {
                                  // Regular field
                                  const fieldName = prop.name ?? key;
                                  fields.push(
                                    prefix
                                      ? `${prefix}.${fieldName}`
                                      : fieldName
                                  );
                                }
                              });
                              return fields;
                            };

                            const allFieldNames = getAllFieldNames(
                              schema.properties
                            );
                            console.log(
                              "👁️ All field names to disable:",
                              allFieldNames
                            );

                            // Disable all fields
                            allFieldNames.forEach(fieldName => {
                              viewForm.setFieldState(fieldName, state => {
                                state.disabled = true;
                                const existingStyle =
                                  state.componentProps?.style ?? {};
                                const existingClassName =
                                  state.componentProps?.className ?? "";
                                state.componentProps = {
                                  ...state.componentProps,
                                  disabled: true,
                                  readOnly: true,
                                  style: {
                                    ...existingStyle,
                                    color: "#707070",
                                  },
                                  className:
                                    `${existingClassName} disabled-field`.trim(),
                                };
                              });
                            });
                          }

                          console.log(
                            "👁️ Opening modal, viewModalOpen will be set to true"
                          );
                          setViewModalOpen(true);
                        }}
                        className="h-8 w-8 p-0"
                        title="View Profile"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* View Profile Modal */}
        <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
          <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>View School Profile</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {schemaJson && (
                <SchemaFormRender form={viewForm} schemaJson={schemaJson} />
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <Button onClick={() => setViewModalOpen(false)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>School Profile</CardTitle>
          <CardDescription>
            Complete your school profile to start submitting activities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {hasSignupData && (
                <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
                  <p>
                    Some fields are pre-filled from your signup and cannot be
                    edited.
                  </p>
                </div>
              )}
              <SchemaFormRender form={form} schemaJson={schemaJson} />
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Submit for Approval"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* View Profile Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View School Profile</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <SchemaFormRender form={viewForm} schemaJson={schemaJson} />
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={() => setViewModalOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
